import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import axios from 'axios'
import {
  ArrowLeft,
  Wallet,
  Gift,
  Users,
  Trophy,
  EyeOff,
  Loader2,
} from 'lucide-react'
import { Button } from '../components/ui/button'
import BrandLogo from '../components/BrandLogo'
import DashboardButton from '../components/DashboardButton'
import LanguageToggle from '../components/LanguageToggle'
import ThemeMenu from '../components/ThemeMenu'
import ProfileButton from '../components/ProfileButton'
import AppFooter from '../components/AppFooter'
import { useLanguage } from '../contexts/LanguageContext'
import { currencySymbol, formatMoney, getApiUrl } from '../lib/utils'
import { wishlistEmoji } from '../lib/wishlistIcons'

const API = getApiUrl()

type Bucket = {
  currency: string
  gross: number
  commission: number
  net: number
  target: number
  gifts: number
  contributors: number
  items: number
  funded_items: number
}
type WalletItem = {
  item_id: string
  title: string
  icon: string
  currency: string
  collected: number
  target: number
  is_funded: boolean
  hidden: boolean
}
type RecentGift = {
  name: string
  amount: number
  currency: string
  item_title: string
  created_at: string | null
}
type WalletData = {
  event_id: string
  event_title: string
  commission_rate: number
  currencies: Bucket[]
  totals: { gifts: number; items: number; funded_items: number }
  items: WalletItem[]
  recent: RecentGift[]
}

const EventWallet = () => {
  const { eventId } = useParams()
  const { language } = useLanguage()
  const tr = (en: string, ka: string) => (language === 'ka' ? ka : en)
  // English inflects the noun after a count; Georgian keeps it invariant.
  const plural = (n: number, enS: string, enP: string, ka: string) =>
    `${n} ${language === 'ka' ? ka : n === 1 ? enS : enP}`

  const [data, setData] = useState<WalletData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<'not_found' | 'error' | null>(null)

  useEffect(() => {
    let active = true
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await axios.get(`${API}/events/${eventId}/wallet`, {
          withCredentials: true,
        })
        if (active) setData(res.data)
      } catch (e: any) {
        if (active) setError(e?.response?.status === 404 ? 'not_found' : 'error')
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => {
      active = false
    }
  }, [eventId])

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(language === 'ka' ? 'ka-GE' : 'en-US', {
        dateStyle: 'medium',
      }),
    [language],
  )
  const relFmt = useMemo(
    () =>
      new Intl.RelativeTimeFormat(language === 'ka' ? 'ka' : 'en', {
        numeric: 'auto',
      }),
    [language],
  )
  const whenLabel = (iso: string | null) => {
    if (!iso) return ''
    const then = new Date(iso).getTime()
    if (Number.isNaN(then)) return ''
    const diff = then - Date.now()
    const abs = Math.abs(diff)
    const MIN = 60_000
    const HR = 3_600_000
    const DAY = 86_400_000
    if (abs < HR) return relFmt.format(Math.round(diff / MIN), 'minute')
    if (abs < DAY) return relFmt.format(Math.round(diff / HR), 'hour')
    if (abs < 30 * DAY) return relFmt.format(Math.round(diff / DAY), 'day')
    return dateFmt.format(new Date(iso))
  }

  const feePct = data
    ? Number((data.commission_rate * 100).toFixed(2)).toString()
    : '0'
  const raisedCurrencies = data?.currencies.filter((c) => c.gross > 0) ?? []
  const hasMoney = raisedCurrencies.length > 0

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen bg-celebrate flex flex-col 2xl:[zoom:1.25]">
      <header className="bg-white/80 dark:bg-[#1e1438]/80 backdrop-blur-xl border-b border-[#1b1033]/10 dark:border-white/10 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link to={`/events/${eventId}`} className="mr-4">
              <Button
                variant="ghost"
                size="icon"
                className="rounded-xl text-[#1b1033] dark:text-[#f4f0ff] hover:bg-[#1b1033]/[0.05] hover:text-[#ff4d8d] transition-colors"
                data-testid="wallet-back-to-event"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <BrandLogo className="mr-4 hidden md:inline-flex" />
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-[#1b1033] dark:text-[#f4f0ff] font-display tracking-tight truncate flex items-center gap-2">
                <Wallet className="w-5 h-5 text-[#ff4d8d] shrink-0" />
                {tr('Wallet', 'საფულე')}
                {data?.event_title ? ` · ${data.event_title}` : ''}
              </h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 ml-4">
              <DashboardButton />
              <LanguageToggle />
              <ThemeMenu />
              <ProfileButton />
            </div>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {children}
      </main>
      <AppFooter />
    </div>
  )

  if (loading) {
    return shell(
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-[#ff4d8d]" />
      </div>,
    )
  }

  if (error || !data) {
    return shell(
      <div className="rounded-2xl border border-[#1b1033]/8 dark:border-white/10 bg-white dark:bg-white/5 p-10 text-center">
        <p className="text-[#1b1033] dark:text-[#f4f0ff] font-medium">
          {error === 'not_found'
            ? tr('Event not found', 'ღონისძიება ვერ მოიძებნა')
            : tr('Could not load the wallet', 'საფულის ჩატვირთვა ვერ მოხერხდა')}
        </p>
        <Link to={`/events/${eventId}`}>
          <Button variant="outline" className="mt-4 rounded-xl">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {tr('Back to event', 'ღონისძიებაზე დაბრუნება')}
          </Button>
        </Link>
      </div>,
    )
  }

  return shell(
    <>
      {/* Currency-agnostic summary chips */}
      <div className="flex flex-wrap gap-3">
        <StatChip
          icon={<Gift className="w-4 h-4" />}
          value={data.totals.gifts.toLocaleString()}
          label={tr('gifts received', 'მიღებული საჩუქარი')}
        />
        <StatChip
          icon={<Trophy className="w-4 h-4" />}
          value={`${data.totals.funded_items}/${data.totals.items}`}
          label={tr('items funded', 'დაფინანსებული ნივთი')}
        />
      </div>

      {!hasMoney ? (
        <div className="rounded-2xl border border-dashed border-[#1b1033]/15 dark:border-white/15 bg-white/60 dark:bg-white/5 p-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#fdeee9] dark:bg-[#281b47]">
            <Wallet className="w-7 h-7 text-[#ff4d8d]" />
          </div>
          <h2 className="text-xl font-semibold text-[#1b1033] dark:text-[#f4f0ff] font-display">
            {tr('No gifts yet', 'ჯერ საჩუქრები არ არის')}
          </h2>
          <p className="mt-1 text-sm text-[#4a3d66] dark:text-[#c3b8dd] max-w-md mx-auto">
            {tr(
              'When guests contribute to your wishlist, the money you raise will show up here.',
              'როცა სტუმრები თქვენს სასურველ ნივთებში შეიტანენ წვლილს, აქ გამოჩნდება შეგროვებული თანხა.',
            )}
          </p>
          <Link to={`/events/${eventId}`}>
            <Button className="btn-gradient text-white rounded-xl mt-5">
              {tr('Manage wishlist', 'სიის მართვა')}
            </Button>
          </Link>
        </div>
      ) : (
        <>
          {/* Per-currency "total raised → fee → net" cards */}
          <div className="grid gap-4 sm:grid-cols-2">
            {raisedCurrencies.map((c) => {
              const sym = currencySymbol(c.currency)
              const pct =
                c.target > 0 ? Math.min(100, (c.gross / c.target) * 100) : 0
              return (
                <div
                  key={c.currency}
                  className="rounded-2xl border border-[#1b1033]/8 dark:border-white/10 bg-white dark:bg-white/5 p-5 shadow-celebrate-sm"
                  data-testid={`wallet-currency-${c.currency}`}
                >
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-[#4a3d66] dark:text-[#c3b8dd]">
                    {tr('Total raised', 'სულ შეგროვდა')}
                  </p>
                  <p className="mt-1 font-display text-4xl font-bold tracking-tight text-[#1b1033] dark:text-[#f4f0ff] tabular-nums">
                    {sym}
                    {formatMoney(c.gross)}
                    <span className="ml-2 text-base font-medium text-[#4a3d66] dark:text-[#c3b8dd]">
                      {c.currency}
                    </span>
                  </p>

                  <div className="mt-4 space-y-2 border-t border-[#1b1033]/8 dark:border-white/10 pt-4 text-sm tabular-nums">
                    <div className="flex items-center justify-between">
                      <span className="text-[#4a3d66] dark:text-[#c3b8dd]">
                        {tr('Storyfy fee', 'Storyfy-ის საკომისიო')} ({feePct}%)
                      </span>
                      <span className="font-medium text-[#4a3d66] dark:text-[#c3b8dd]">
                        −{sym}
                        {formatMoney(c.commission)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-[#ff4d8d]/[0.07] dark:bg-[#ff4d8d]/10 px-3 py-2">
                      <span className="font-semibold text-[#1b1033] dark:text-[#f4f0ff]">
                        {tr('Your net payout', 'თქვენი წმინდა თანხა')}
                      </span>
                      <span className="font-bold text-[#ff4d8d] text-base">
                        {sym}
                        {formatMoney(c.net)}
                      </span>
                    </div>
                  </div>

                  {c.target > 0 && (
                    <div className="mt-4">
                      <div className="h-2 w-full overflow-hidden rounded-full bg-[#f1ecf6] dark:bg-[#281b47]">
                        <div
                          className="h-full rounded-full bg-grad-hot transition-[width] duration-700 ease-out"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <p className="mt-1.5 text-xs text-[#4a3d66] dark:text-[#c3b8dd] tabular-nums">
                        {sym}
                        {formatMoney(c.gross)} {tr('of', '/')} {sym}
                        {formatMoney(c.target)} {tr('target', 'სამიზნე')}
                      </p>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#4a3d66] dark:text-[#c3b8dd]">
                    <span className="inline-flex items-center gap-1">
                      <Gift className="w-3.5 h-3.5" />
                      {plural(c.gifts, 'gift', 'gifts', 'საჩუქარი')}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {plural(c.contributors, 'contributor', 'contributors', 'შემომტანი')}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Trophy className="w-3.5 h-3.5" />
                      {c.funded_items}/{c.items} {tr('funded', 'დაფინანსდა')}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Per-item breakdown */}
          <section className="rounded-2xl border border-[#1b1033]/8 dark:border-white/10 bg-white dark:bg-white/5 overflow-hidden">
            <div className="p-4 border-b border-[#1b1033]/8 dark:border-white/10">
              <h2 className="text-lg font-semibold text-[#1b1033] dark:text-[#f4f0ff] font-display">
                {tr('By item', 'ნივთების მიხედვით')}
              </h2>
            </div>
            <ul className="divide-y divide-[#1b1033]/6 dark:divide-white/8">
              {data.items.map((it) => {
                const sym = currencySymbol(it.currency)
                const pct =
                  it.target > 0
                    ? Math.min(100, (it.collected / it.target) * 100)
                    : it.collected > 0
                      ? 100
                      : 0
                return (
                  <li
                    key={it.item_id}
                    className="flex items-center gap-3 p-4"
                    data-testid={`wallet-item-${it.item_id}`}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#1b1033]/[0.04] dark:bg-white/10 text-lg leading-none">
                      {wishlistEmoji(it.icon)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate font-medium text-[#1b1033] dark:text-[#f4f0ff]">
                          {it.title}
                        </p>
                        {it.is_funded && (
                          <span className="shrink-0 rounded-full bg-emerald-100 dark:bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                            {tr('Funded', 'დაფინანსდა')}
                          </span>
                        )}
                        {it.hidden && (
                          <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#1b1033]/5 dark:bg-white/10 px-2 py-0.5 text-[10px] font-medium text-[#4a3d66] dark:text-[#c3b8dd]">
                            <EyeOff className="w-3 h-3" />
                            {tr('Hidden', 'დამალული')}
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-[#f1ecf6] dark:bg-[#281b47]">
                        <div
                          className="h-full rounded-full bg-grad-hot"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    <div className="shrink-0 text-right tabular-nums">
                      <p className="text-sm font-semibold text-[#1b1033] dark:text-[#f4f0ff]">
                        {sym}
                        {formatMoney(it.collected)}
                      </p>
                      {it.target > 0 && (
                        <p className="text-xs text-[#4a3d66] dark:text-[#c3b8dd]">
                          {tr('of', '/')} {sym}
                          {formatMoney(it.target)}
                        </p>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          </section>

          {/* Recent gifts */}
          {data.recent.length > 0 && (
            <section className="rounded-2xl border border-[#1b1033]/8 dark:border-white/10 bg-white dark:bg-white/5 overflow-hidden">
              <div className="p-4 border-b border-[#1b1033]/8 dark:border-white/10">
                <h2 className="text-lg font-semibold text-[#1b1033] dark:text-[#f4f0ff] font-display">
                  {tr('Recent gifts', 'ბოლო საჩუქრები')}
                </h2>
              </div>
              <ul className="divide-y divide-[#1b1033]/6 dark:divide-white/8">
                {data.recent.map((g, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-3 p-4"
                    data-testid="wallet-recent-gift"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-grad-hot text-white">
                      <Gift className="w-4 h-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-[#1b1033] dark:text-[#f4f0ff]">
                        <span className="font-semibold">{g.name}</span>{' '}
                        {tr('gifted', 'გაიღო')}{' '}
                        <span className="font-semibold tabular-nums">
                          {currencySymbol(g.currency)}
                          {formatMoney(g.amount)}
                        </span>
                        {g.item_title ? (
                          <span className="text-[#4a3d66] dark:text-[#c3b8dd]">
                            {' '}
                            · {g.item_title}
                          </span>
                        ) : null}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs text-[#4a3d66] dark:text-[#c3b8dd]">
                      {whenLabel(g.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </>,
  )
}

const StatChip = ({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode
  value: string
  label: string
}) => (
  <div className="inline-flex items-center gap-2.5 rounded-2xl border border-[#1b1033]/8 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2.5">
    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#fdeee9] dark:bg-[#281b47] text-[#ff4d8d]">
      {icon}
    </span>
    <span className="leading-tight">
      <span className="block font-display text-lg font-bold text-[#1b1033] dark:text-[#f4f0ff] tabular-nums">
        {value}
      </span>
      <span className="block text-xs text-[#4a3d66] dark:text-[#c3b8dd]">
        {label}
      </span>
    </span>
  </div>
)

export default EventWallet
